<template>
  <section class="map-page" :dir="isArabic ? 'rtl' : 'ltr'">
    <div class="section-head">
      <h2>{{ t("facilityMap.title") }}</h2>
      <p>{{ t("facilityMap.description") }}</p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-wrapper">
      <div class="loading-card">
        <v-progress-circular
          indeterminate
          size="70"
          width="6"
          color="primary"
        />

        <h3>{{ t("facilityMap.loadingTitle") }}</h3>
        <p>{{ t("facilityMap.loadingDescription") }}</p>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="errorMessage" class="error-box">
      {{ errorMessage }}
    </div>

    <!-- Map -->
    <div v-else class="map-card">
      <div id="facilityMap" class="real-map"></div>

      <div class="legend">
        <div>
          <span class="dot critical"></span>
          {{ t("facilityMap.priorities.P1") }}
        </div>

        <div>
          <span class="dot high"></span>
          {{ t("facilityMap.priorities.P2") }}
        </div>

        <div>
          <span class="dot medium"></span>
          {{ t("facilityMap.priorities.P3") }}
        </div>

        <div>
          <span class="dot low"></span>
          {{ t("facilityMap.priorities.P4") }}
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/plugins/api";

const router = useRouter();
const { t, locale } = useI18n();

/*
|--------------------------------------------------------------------------
| State
|--------------------------------------------------------------------------
*/

const loading = ref(true);
const errorMessage = ref("");
const rawFacilities = ref([]);

let map = null;

const centerLat = 12.8813;
const centerLng = 44.9992;

const isArabic = computed(() => locale.value === "ar");

/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

const normalizeNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : null;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "-";
  }

  return number.toLocaleString(isArabic.value ? "ar-YE" : "en-US");
};

const formatFis = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0.0";
  }

  return number.toFixed(1);
};

const safeText = (value, fallback = "-") => {
  return value !== null && value !== undefined && value !== ""
    ? String(value)
    : fallback;
};

/*
|--------------------------------------------------------------------------
| Escape HTML used inside Leaflet popups
|--------------------------------------------------------------------------
*/

const escapeHtml = (value) => {
  return safeText(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

/*
|--------------------------------------------------------------------------
| Fake coordinates
|--------------------------------------------------------------------------
|
| تستخدم فقط إذا لم تكن الإحداثيات موجودة في API.
|
*/

const getFakeCoordinate = (index) => {
  const angle = index * 0.75;
  const radius = 0.006 + index * 0.0012;

  return {
    lat: centerLat + Math.sin(angle) * radius,

    lng: centerLng + Math.cos(angle) * radius,
  };
};

/*
|--------------------------------------------------------------------------
| Priority
|--------------------------------------------------------------------------
*/

const getPriorityCode = (item) => {
  return item.priority_code || item.priority || item.periority || "P4";
};

const getPriorityInfo = (priority) => {
  const values = {
    P1: {
      text: t("facilityMap.priorityNames.P1"),
      color: "#dc2626",
      className: "critical",
    },

    P2: {
      text: t("facilityMap.priorityNames.P2"),
      color: "#f59e0b",
      className: "high",
    },

    P3: {
      text: t("facilityMap.priorityNames.P3"),
      color: "#2563eb",
      className: "medium",
    },

    P4: {
      text: t("facilityMap.priorityNames.P4"),
      color: "#16a34a",
      className: "low",
    },
  };

  return values[priority] || values.P4;
};

/*
|--------------------------------------------------------------------------
| Normalize API facility
|--------------------------------------------------------------------------
*/

const normalizeFacility = (item, index) => {
  let lat = normalizeNumber(item.gps_latitude ?? item.latitude ?? item.lat);

  let lng = normalizeNumber(
    item.gps_longitude ?? item.longitude ?? item.lng ?? item.lon
  );

  if (lat === null || lng === null) {
    const fake = getFakeCoordinate(index);

    lat = fake.lat;
    lng = fake.lng;
  }

  const priority = getPriorityCode(item);

  const priorityInfo = getPriorityInfo(priority);

  const name = isArabic.value
    ? item.facility_name_ar ||
      item.facility_name_en ||
      item.facility_name ||
      "-"
    : item.facility_name_en ||
      item.facility_name_ar ||
      item.facility_name ||
      "-";

  const type = isArabic.value
    ? item.facility_type_ar ||
      item.facility_type_en ||
      item.facility_type ||
      "-"
    : item.facility_type_en ||
      item.facility_type_ar ||
      item.facility_type ||
      "-";

  const governorate = isArabic.value
    ? item.governorate_name_ar ||
      item.governorate_name_en ||
      item.governorate ||
      "-"
    : item.governorate_name_en ||
      item.governorate_name_ar ||
      item.governorate ||
      "-";

  const district = isArabic.value
    ? item.district_name_ar || item.district_name_en || item.district || "-"
    : item.district_name_en || item.district_name_ar || item.district || "-";

  const operationalStatus = isArabic.value
    ? item.operational_status_ar ||
      item.operational_status_en ||
      item.operational_status ||
      "-"
    : item.operational_status_en ||
      item.operational_status_ar ||
      item.operational_status ||
      "-";

  return {
    id: item.survey_id || item.id || index + 1,

    name,
    type,
    governorate,
    district,
    operationalStatus,

    staff: item.cadre_total_count ?? item.total_staff ?? 0,

    beneficiaries: item.beneficiaries_current ?? item.beneficiaries_month ?? 0,

    population: item.target_population ?? 0,

    fis: formatFis(item.FIS_result ?? item.FIS ?? item.fis ?? 0),

    lat,
    lng,

    priority,
    priorityText: priorityInfo.text,

    priorityColor: priorityInfo.color,

    priorityClass: priorityInfo.className,
  };
};

/*
|--------------------------------------------------------------------------
| Facilities according to selected language
|--------------------------------------------------------------------------
*/

const facilities = computed(() => {
  return rawFacilities.value.map((item, index) =>
    normalizeFacility(item, index)
  );
});

/*
|--------------------------------------------------------------------------
| Load data
|--------------------------------------------------------------------------
*/

const loadFacilities = async () => {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await api.get("/health-facilities");

    const data = response.data?.data || response.data || [];

    if (!Array.isArray(data)) {
      throw new Error("Invalid API response");
    }

    rawFacilities.value = data;

    if (data.length === 0) {
      errorMessage.value = t("facilityMap.noFacilities");
    }
  } catch (error) {
    console.error("Failed to load facility map:", error);

    errorMessage.value = t("facilityMap.loadError");
  } finally {
    loading.value = false;
  }
};

/*
|--------------------------------------------------------------------------
| Marker icon
|--------------------------------------------------------------------------
*/

const createFacilityIcon = (facility) => {
  return L.divIcon({
    className: "custom-marker",

    html: `
      <div
        class="facility-marker"
        dir="${isArabic.value ? "rtl" : "ltr"}"
      >
        <div
          class="marker-dot marker-${escapeHtml(facility.priority)}"
          style="background:${escapeHtml(facility.priorityColor)}"
        ></div>

        <div class="marker-label">
          ${escapeHtml(facility.name)}
        </div>
      </div>
    `,

    iconSize: [250, 50],
    iconAnchor: [22, 25],
    popupAnchor: [0, -25],
  });
};

/*
|--------------------------------------------------------------------------
| Popup
|--------------------------------------------------------------------------
*/

const createPopupHtml = (facility) => {
  const direction = isArabic.value ? "rtl" : "ltr";

  const textAlign = isArabic.value ? "right" : "left";

  return `
    <div
      class="facility-popup-content"
      dir="${direction}"
      style="
        text-align:${textAlign};
        min-width:240px;
        font-family:Cairo;
      "
    >
      <strong
        style="
          display:block;
          margin-bottom:8px;
          font-size:15px;
          color:#0f172a;
        "
      >
        ${escapeHtml(facility.name)}
      </strong>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.type"))}:</span>

        <strong>
          ${escapeHtml(facility.type)}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.governorate"))}:</span>

        <strong>
          ${escapeHtml(facility.governorate)}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.district"))}:</span>

        <strong>
          ${escapeHtml(facility.district)}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.operationalStatus"))}:</span>

        <strong>
          ${escapeHtml(facility.operationalStatus)}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.staff"))}:</span>

        <strong>
          ${escapeHtml(formatNumber(facility.staff))}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.beneficiaries"))}:</span>

        <strong>
          ${escapeHtml(formatNumber(facility.beneficiaries))}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.population"))}:</span>

        <strong>
          ${escapeHtml(formatNumber(facility.population))}
        </strong>
      </div>

      <div class="popup-row">
        <span>FIS:</span>

        <strong>
          ${escapeHtml(facility.fis)}
        </strong>
      </div>

      <div class="popup-row">
        <span>${escapeHtml(t("facilityMap.popup.priority"))}:</span>

        <strong
          style="
            color:${escapeHtml(facility.priorityColor)}
          "
        >
          ${escapeHtml(facility.priority)}
          -
          ${escapeHtml(facility.priorityText)}
        </strong>
      </div>

      <button
        type="button"
        class="popup-btn"
        data-id="${escapeHtml(facility.id)}"
      >
        ${escapeHtml(t("facilityMap.popup.viewDetails"))}
      </button>
    </div>
  `;
};

/*
|--------------------------------------------------------------------------
| Initialize Leaflet map
|--------------------------------------------------------------------------
*/

const initMap = async () => {
  await nextTick();

  const mapElement = document.getElementById("facilityMap");

  if (!mapElement) {
    return;
  }

  if (map) {
    map.remove();
    map = null;
  }

  map = L.map("facilityMap", {
    zoomControl: true,
    attributionControl: true,
  }).setView([centerLat, centerLng], 13);

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    maxZoom: 19,
    attribution: "&copy; OpenStreetMap",
  }).addTo(map);

  const bounds = [];

  facilities.value.forEach((facility) => {
    const marker = L.marker([facility.lat, facility.lng], {
      icon: createFacilityIcon(facility),
    }).addTo(map);

    marker.bindPopup(createPopupHtml(facility), {
      maxWidth: 320,
    });

    marker.on("popupopen", () => {
      setTimeout(() => {
        const button = document.querySelector(
          `.popup-btn[data-id="${facility.id}"]`
        );

        if (button) {
          button.onclick = () => {
            router.push(`/facilities/${facility.id}`);
          };
        }
      }, 0);
    });

    bounds.push([facility.lat, facility.lng]);
  });

  if (bounds.length > 0) {
    map.fitBounds(bounds, {
      padding: [45, 45],
      maxZoom: 15,
    });
  }

  setTimeout(() => {
    map?.invalidateSize();
  }, 300);
};

/*
|--------------------------------------------------------------------------
| Lifecycle
|--------------------------------------------------------------------------
*/

onMounted(async () => {
  await loadFacilities();

  if (!errorMessage.value && facilities.value.length > 0) {
    await initMap();
  }
});

/*
|--------------------------------------------------------------------------
| Rebuild map after language change
|--------------------------------------------------------------------------
|
| لأن نصوص العلامات والنوافذ المنبثقة يتم إنشاؤها كـ HTML داخل Leaflet.
|
*/

watch(locale, async () => {
  if (loading.value || errorMessage.value || rawFacilities.value.length === 0) {
    return;
  }

  await initMap();
});

onBeforeUnmount(() => {
  if (map) {
    map.remove();
    map = null;
  }
});
</script>

<style scoped>
.map-page {
  min-height: 100vh;
  padding: 30px 32px 70px;
  color: #1a2433;
  background: #f4f6f9;
  font-family: Cairo;
}

/*
|--------------------------------------------------------------------------
| Header
|--------------------------------------------------------------------------
*/

.section-head {
  margin-bottom: 30px;
  text-align: center;
}

.section-head h2 {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 800;
}

.section-head p {
  margin: 0;
  color: #4c5b6e;
  font-size: 14px;
}

/*
|--------------------------------------------------------------------------
| Loading
|--------------------------------------------------------------------------
*/

.loading-wrapper {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.93);
  backdrop-filter: blur(5px);
}

.loading-card {
  width: 320px;
  max-width: 90%;
  padding: 34px 24px;
  text-align: center;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 20px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
}

.loading-card h3 {
  margin: 18px 0 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 800;
}

.loading-card p {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 13px;
}

/*
|--------------------------------------------------------------------------
| Error
|--------------------------------------------------------------------------
*/

.error-box {
  padding: 25px;
  color: #b91c1c;
  text-align: center;
  font-size: 14px;
  font-weight: 800;
  background: #fff1f1;
  border: 1px solid #fecaca;
  border-radius: 10px;
}

/*
|--------------------------------------------------------------------------
| Map
|--------------------------------------------------------------------------
*/

.map-card {
  padding: 18px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(13, 34, 64, 0.08);
}

.real-map {
  z-index: 1;
  width: 100%;
  height: 650px;
  overflow: hidden;
  border-radius: 10px;
}

/*
|--------------------------------------------------------------------------
| Legend
|--------------------------------------------------------------------------
*/

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  color: #4c5b6e;
  font-size: 13px;
}

.legend div {
  display: flex;
  align-items: center;
  gap: 7px;
}

.dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
}

.dot.critical {
  background: #dc2626;
}

.dot.high {
  background: #e58a1f;
}

.dot.medium {
  background: #4074e8;
}

.dot.low {
  background: #16a34a;
}

/*
|--------------------------------------------------------------------------
| Leaflet markers
|--------------------------------------------------------------------------
*/

:deep(.custom-marker) {
  background: transparent !important;
  border: none !important;
}

:deep(.facility-marker) {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

:deep(.facility-marker[dir="rtl"]) {
  flex-direction: row;
}

:deep(.facility-marker[dir="ltr"]) {
  flex-direction: row;
}

:deep(.marker-dot) {
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
}

:deep(.marker-label) {
  padding: 5px 10px;
  color: #172033;
  font-size: 12px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
}

/*
|--------------------------------------------------------------------------
| Leaflet popup
|--------------------------------------------------------------------------
*/

:deep(.leaflet-popup-content) {
  margin: 15px 18px;
}

.map-page[dir="rtl"] :deep(.leaflet-popup-content) {
  direction: rtl;
  text-align: right;
}

.map-page[dir="ltr"] :deep(.leaflet-popup-content) {
  direction: ltr;
  text-align: left;
}

:deep(.popup-row) {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  color: #475569;
  font-size: 12px;
  border-bottom: 1px solid #eef2f7;
}

:deep(.popup-row strong) {
  color: #172033;
  font-weight: 800;
}

:deep(.popup-btn) {
  width: 100%;
  padding: 8px 12px;
  margin-top: 12px;
  color: #fff;
  font-family: Cairo;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  background: #173f73;
  border: none;
  border-radius: 7px;
}

:deep(.popup-btn:hover) {
  background: #0f315d;
}

/*
|--------------------------------------------------------------------------
| Responsive
|--------------------------------------------------------------------------
*/

@media (max-width: 900px) {
  .map-page {
    padding: 20px 14px;
  }

  .real-map {
    height: 500px;
  }

  :deep(.marker-label) {
    padding: 4px 8px;
    font-size: 11px;
  }
}

@media (max-width: 600px) {
  .real-map {
    height: 450px;
  }

  .map-card {
    padding: 10px;
  }

  .legend {
    gap: 10px;
  }
}
</style>
